import React, { createContext, useContext, useMemo, useState } from "react";

/** Shared intent keys across app */
export type IntentKey = "finance" | "health" | "relationship" | "productivity" | "mindfulness";

/** Central palette map (OK to tweak here) */
export const intentPalettes: Record<IntentKey, React.CSSProperties> = {
  finance:      { ["--c1" as any]: "#22d3ee", ["--c2" as any]: "#0ea5e9", ["--c3" as any]: "#06b6d4", ["--c4" as any]: "#14b8a6", ["--aurora-speed" as any]: "9s" },
  health:       { ["--c1" as any]: "#34d399", ["--c2" as any]: "#22c55e", ["--c3" as any]: "#10b981", ["--c4" as any]: "#84cc16", ["--aurora-speed" as any]: "10s" },
  relationship: { ["--c1" as any]: "#f472b6", ["--c2" as any]: "#e879f9", ["--c3" as any]: "#fb7185", ["--c4" as any]: "#f59e0b", ["--aurora-speed" as any]: "8.5s" },
  productivity: { ["--c1" as any]: "#60a5fa", ["--c2" as any]: "#8b5cf6", ["--c3" as any]: "#38bdf8", ["--c4" as any]: "#22d3ee", ["--aurora-speed" as any]: "8s" },
  mindfulness:  { ["--c1" as any]: "#a7f3d0", ["--c2" as any]: "#6ee7b7", ["--c3" as any]: "#93c5fd", ["--c4" as any]: "#c7d2fe", ["--aurora-speed" as any]: "11s" },
};

type IntentThemeCtx = {
  currentIntent: IntentKey;
  setCurrentIntent: (k: IntentKey) => void;
  palettes: typeof intentPalettes;
};

const Ctx = createContext<IntentThemeCtx | null>(null);

export function IntentThemeProvider({ initial = "productivity", children }: { initial?: IntentKey; children: React.ReactNode }) {
  const [currentIntent, setCurrentIntent] = useState<IntentKey>(initial);
  const value = useMemo<IntentThemeCtx>(() => ({ currentIntent, setCurrentIntent, palettes: intentPalettes }), [currentIntent]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useIntentTheme() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useIntentTheme must be used within <IntentThemeProvider>");
  return v;
}

/** Reusable frame that reads current intent�s palette or accepts an override */
export function AuroraFrame({
  variant = "animated", // "animated" | "border"
  radius = "1rem",
  width = "2px",
  palette,
  className = "",
  children,
  style,
}: {
  variant?: "animated" | "border";
  radius?: string;
  width?: string;
  palette?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const { currentIntent, palettes } = useIntentTheme();
  const base = palette ?? palettes[currentIntent];
  const merged: React.CSSProperties = {
    ...base,
    ["--aurora-radius" as any]: radius,
    ["--aurora-width" as any]: width,
    ...style,
  };
  const shell = variant === "animated" ? "aurora-wrap aurora-animated" : "aurora-wrap aurora-border";
  return (
    <div className={`${shell} ${className}`} style={merged}>
      {children}
    </div>
  );
}
